import { useEffect, useState } from "react";
import { Navigate, useNavigate, useLocation } from "react-router-dom";
import { fetchUserServiceProviders, fetchUserServiceProviderById } from "@/services/providers/providerOwnerService";
import { useAuth } from "@/contexts/auth";
import Layout from "@/components/Layout";
import ListingForm from "@/components/ListingForm";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { useQuery } from "@tanstack/react-query";
import { ServiceProviderRow } from "@/types/supabase";
import SubscriptionBanner from "@/components/SubscriptionBanner";
import { useSubscription } from "@/hooks/subscription";
import { toast } from "sonner";
import { debugLogger } from "@/utils/debug";
import { useTranslation } from "react-i18next";
import { platformApiFetch } from '@/services/platformApi/client';
import PageHero from "@/components/layout/PageHero";
import {
  AlertCircle,
  ChevronLeft,
  CheckCircle,
  FileText,
  Info,
  Mail
} from "lucide-react";

const SubmitListingPage = () => {
  const { t, i18n } = useTranslation('submitListing');
  const {
    user,
    session,
    loading,
    isAdmin
  } = useAuth();
  const [showSuccess, setShowSuccess] = useState(false);
  const [sendingVerification, setSendingVerification] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [listingId, setListingId] = useState<string | null>(null);
  const navigate = useNavigate();
  const {
    toast: toastHook
  } = useToast();
  const location = useLocation();
  const {
    data: subscriptionStatus
  } = useSubscription();
  const userTier = subscriptionStatus?.tier || 'free_tier';
  const isPremium = userTier === 'premium_tier' || isAdmin;
  const isFreeTier = userTier === 'free_tier';
  const isWhatsAppTier = userTier === 'whatsapp_tier';
  const hasConfirmedEmail = Boolean(user?.email_confirmed_at);
  const hasConfirmedPhone = Boolean(user?.phone_confirmed_at);
  const canSubmitListing = hasConfirmedEmail || hasConfirmedPhone;
  const shouldShowEmailVerificationNotice = !canSubmitListing && Boolean(user?.email);

  // Check if current language is Chinese to show language note
  const isChineseLanguage = i18n.language === 'zh-TW' || i18n.language === 'zh-CN';
  
  const {
    data: existingListings,
    isLoading: checkingExistingListings
  } = useQuery({
    queryKey: ['userListings', user?.id],
    queryFn: async () => {
      if (!user) return [];
      return await fetchUserServiceProviders(user.id);
    },
    enabled: !!user
  });
  
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const editId = params.get('edit');
    if (editId) {
      setIsEditing(true);
      setListingId(editId);
    }
  }, [location]);
  
  const {
    data: listingData,
    isLoading: listingLoading,
    error: listingError
  } = useQuery({
    queryKey: ['listingToEdit', listingId],
    queryFn: async () => {
      if (!listingId || !user) return null;
      debugLogger.log(`Fetching listing data for ID: ${listingId}`);
      try {
        const data = await fetchUserServiceProviderById(listingId, user.id);
        debugLogger.log("Listing data fetched:", data);
        return data as ServiceProviderRow;
      } catch (error) {
        console.error("Error fetching listing:", error);
        toast.error("Error fetching listing: " + (error as Error).message);
        return null;
      }
    },
    enabled: !!listingId && !!user
  });
  
  const handleResendVerification = async () => {
    if (!user?.email) return;
    try {
      setSendingVerification(true);
      await platformApiFetch<{ success: boolean }>('/v1/auth/email-verification', {
        method: 'POST',
        body: JSON.stringify({
          email: user.email,
        }),
      });
      toast.success("Verification email sent");
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      toast.error("Error sending verification email: " + message);
    } finally {
      setSendingVerification(false);
    }
  };

  // Starter and WhatsApp Verified users are limited to one listing.
  const hasPendingListing = !isPremium && existingListings?.some(listing => listing.status === 'pending');
  const hasRejectedListing = existingListings?.some(listing => listing.status === 'rejected');
  const hasApprovedListing = existingListings?.some(listing => listing.status === 'approved');
  const pendingListing = existingListings?.find(listing => listing.status === 'pending');
  const rejectedListing = existingListings?.find(listing => listing.status === 'rejected');
  const approvedListing = existingListings?.find(listing => listing.status === 'approved');
  const existingListingActionTarget = pendingListing ?? approvedListing ?? rejectedListing;
  const shouldShowUpdateListingCta = isWhatsAppTier && Boolean(existingListingActionTarget?.id);

  // Starter and WhatsApp Verified users can only have one listing.
  const hasExistingListing = !isPremium && existingListings && existingListings.length >= 1;
  
  const handleListingSubmitted = async () => {
    if (!isPremium && hasExistingListing && !isEditing) {
      toast.error("Starter and WhatsApp Verified accounts include one listing. Upgrade to Premium for unlimited listings.");
      return;
    }

    setShowSuccess(true);
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const handleExitEdit = () => {
    if (window.history.state && window.history.state.idx > 0) {
      navigate(-1);
      return;
    }

    navigate("/user/listings", { replace: true });
  };
  
  useEffect(() => {
    if (listingId && !listingLoading && !listingData && !loading && user) {
      toast.error("The listing you're trying to edit doesn't exist or you don't have permission to edit it.");
      navigate("/my-listings");
    }
  }, [listingId, listingLoading, listingData, loading, user, navigate]);
  
  if (loading || checkingExistingListings) {
    return <Layout>
        <div className="bg-brand-mist">
        <PageHero title={isEditing ? "Edit Listing" : t("title")} description={t("description")} contentClassName="py-12 md:py-16" />
        <div className="container mx-auto px-4 py-16 text-center">
          <div className="animate-pulse">
            <div className="h-8 bg-brand-cloud rounded w-1/3 mx-auto mb-4"></div>
            <div className="h-4 bg-brand-cloud rounded w-1/2 mx-auto"></div>
          </div>
        </div>
        </div>
      </Layout>;
  }
  
  if (!user) {
    return <Navigate to="/auth?redirect=/submit-listing" replace />;
  }

  // Redirect non-premium users who already have a listing (approved, pending, or rejected)
  // to the proper page when they try to access the submit listing page
  if (!isEditing && !isPremium && (hasPendingListing || hasRejectedListing || hasApprovedListing)) {
    return <Layout>
        <div className="bg-brand-mist">
          <PageHero
            title="Existing Listing Found"
            description="Starter and WhatsApp Verified accounts can keep one listing active at a time."
            contentClassName="py-12 md:py-16"
          />
          <div className="container mx-auto px-4 py-10 md:py-14">
            <div className="max-w-3xl mx-auto">
              <section className="overflow-hidden rounded-md border border-brand-cloud bg-white text-center shadow-sm">
                <div className="border-b border-brand-cloud p-8">
                  <FileText className="mx-auto mb-4 h-14 w-14 text-harbour" />
                
                {hasPendingListing && <>
                    <h2 className="mb-2 text-center public-section-title">
                      Listing Under Review
                    </h2>
                    <p className="mb-6 text-center text-brand-slate">
                      Your listing "{pendingListing?.name}" is currently being reviewed and will be live soon.
                    </p>
                  </>}
                
                {!hasPendingListing && hasApprovedListing && <>
                    <h2 className="mb-2 text-center public-section-title">
                      Active Listing Found
                    </h2>
                    <p className="mb-6 text-center text-brand-slate">
                      You already have an active listing. Starter and WhatsApp Verified accounts include one listing.
                    </p>
                  </>}
                
                {!hasPendingListing && !hasApprovedListing && hasRejectedListing && <>
                    <h2 className="mb-2 text-center public-section-title">
                      Listing Needs Updates
                    </h2>
                    <p className="mb-6 text-center text-brand-slate">
                      Your listing "{rejectedListing?.name}" needs some updates before it can go live.
                    </p>
                  </>}
                </div>
                
                <div className="flex flex-wrap justify-center gap-4 p-6">
                  <Button onClick={() => navigate("/user/listings")}>
                    View My Listings
                  </Button>
                  
                  {!shouldShowUpdateListingCta && hasRejectedListing && <Button variant="outline" onClick={() => navigate(`/submit-listing?edit=${rejectedListing?.id}`)}>
                      Edit Rejected Listing
                    </Button>}

                  {shouldShowUpdateListingCta ? (
                    <Button variant="secondary" onClick={() => navigate(`/submit-listing?edit=${existingListingActionTarget?.id}`)}>
                      Update Your Listing
                    </Button>
                  ) : (
                    <Button variant="secondary" onClick={() => navigate("/pricing")}>
                      Upgrade subscription
                    </Button>
                  )}
                </div>
              </section>
            </div>
          </div>
        </div>
      </Layout>;
  }
  
  return <Layout>
      <div className="bg-brand-mist">
        <PageHero
          title={isEditing ? "Edit listing" : t("title")}
          description={isEditing ? "Update your listing details within your plan limits" : t("description")}
          contentClassName="py-12 md:py-16"
        />
        <div className="container mx-auto px-4 py-10 md:py-14">
          <div className="max-w-3xl mx-auto">
            {!isPremium && !isAdmin && !isEditing && <div className="mb-6">
                <SubscriptionBanner
                  title={shouldShowUpdateListingCta ? "Update your provider listing" : undefined}
                  description={shouldShowUpdateListingCta
                    ? "WhatsApp Verified now includes up to four service areas. Update your existing listing to use the additional coverage in your current plan."
                    : undefined}
                  actionLabel={shouldShowUpdateListingCta ? "Update Your Listing" : undefined}
                  onAction={shouldShowUpdateListingCta ? () => navigate(`/submit-listing?edit=${existingListingActionTarget?.id}`) : undefined}
                />
              </div>}
            
            {!isEditing && hasExistingListing && !isAdmin && !isPremium && <Alert className="mb-6 border-red-200 bg-red-50">
                <AlertCircle className="h-4 w-4 text-red-600" />
                <AlertDescription className="text-red-800">
                  {shouldShowUpdateListingCta
                    ? "You already have a listing tied to your account. Update it to use the allowances included with your current plan."
                    : "You have reached the listing limit for your current subscription. Upgrade to Premium for unlimited listings."}
                </AlertDescription>
              </Alert>}
            
            {isFreeTier && <Alert className="mb-6 rounded-md border-harbour bg-harbour-soft">
                <Info className="h-4 w-4 text-harbour" />
                <AlertTitle className="font-medium text-brand-ink">Starter plan limits</AlertTitle>
                <AlertDescription className="text-brand-slate">
                  Your Starter plan includes:
                  <ul className="list-disc pl-5 mt-2">
                    <li>One active listing at a time</li>
                    <li>One category per listing</li>
                    <li>Up to two service areas</li>
                  </ul>
                </AlertDescription>
              </Alert>}

            {isChineseLanguage && <Alert className="mb-6 rounded-md border-amber bg-amber-soft">
              <Info className="h-4 w-4 text-amber-600" />
              <AlertDescription className="text-brand-slate">
                {t("languageNote")}
              </AlertDescription>
            </Alert>}
            
            {showSuccess ? 
              <section className="overflow-hidden rounded-md border border-brand-cloud bg-white text-center shadow-sm">
                <div className="border-b border-brand-cloud p-8">
                  <CheckCircle className="mx-auto mb-4 h-14 w-14 text-teal" />
                <h2 className="mb-2 public-section-title">
                  {isEditing ? "Listing updated" : "Listing submitted"}
                </h2>
                <p className="mb-6 text-brand-slate">
                  {isEditing ? "Your listing changes have been saved and will be reviewed." : "Your listing has been submitted for review before it goes live in the directory."}
                </p>
                </div>
                <div className="flex flex-wrap justify-center gap-4 p-6">
                  <Button onClick={() => navigate("/user/listings")}>
                    View My Listings
                  </Button>
                  <Button variant="outline" onClick={() => navigate("/")}>
                    Return Home
                  </Button>
                </div>
              </section>
            : 
              <>
                <div className="mb-6">
                  {isEditing && <Button variant="ghost" className="mb-4 px-0 text-brand-slate hover:bg-transparent hover:text-brand-ink" onClick={handleExitEdit}>
                      <ChevronLeft className="mr-1 h-4 w-4" />
                      Back
                    </Button>}
                </div>
                
                {shouldShowEmailVerificationNotice && <Alert className="mb-6 rounded-md border-amber bg-amber-soft">
                    <Mail className="h-4 w-4 text-amber-600" />
                    <AlertDescription className="flex flex-col gap-2 text-brand-slate sm:flex-row sm:items-center">
                      <span>Please verify your email address before submitting a listing.</span>
                      <Button variant="outline" size="sm" onClick={handleResendVerification} disabled={sendingVerification} className="whitespace-nowrap mt-2 sm:mt-0">
                        {sendingVerification ? "Sending..." : "Resend Verification"}
                      </Button>
                    </AlertDescription>
                  </Alert>}
                
                {isEditing && listingLoading ? <div className="rounded-md border border-brand-cloud bg-white p-8 shadow-sm">
                    <div className="animate-pulse space-y-4">
                      <div className="h-6 bg-brand-cloud rounded w-1/4"></div>
                      <div className="h-4 bg-brand-cloud rounded w-1/2"></div>
                      <div className="h-32 bg-brand-cloud rounded"></div>
                    </div>
                  </div> : <ListingForm isDisabled={!canSubmitListing || !isPremium && hasExistingListing && !isEditing && !isAdmin} onSubmitSuccess={handleListingSubmitted} editMode={isEditing} initialData={listingData} isPremium={isPremium} isAdmin={isAdmin} onCancel={isEditing ? handleExitEdit : undefined} reviewNotice={isEditing ? "Your listing changes will be reviewed before going live." : t("alerts.reviewRequired")} />}
              </>
            }
          </div>
        </div>
      </div>
    </Layout>;
};

export default SubmitListingPage;
